import { B2Sunrise } from '@e3dc-react/icons'
import { SuspenseContentFallback } from '@e3dc-react/shell/Elements/Suspense'
import { Box, Button, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { isFlowTheme } from 'src/Libs/utils'
import e3dcLightBackground from 'src/Resources/Images/logos/e3dc-light-background.svg'
import {
  MAINTENANCE_LOGO,
  BUTTON_TEXT_GERMAN,
  FOOTER_ENGLISH_1,
  FOOTER_ENGLISH_2,
  SUBTITLE_GERMAN_1,
  SUBTITLE_GERMAN_2,
  TITLE_GERMAN,
} from './consts'

const useStyles = makeStyles()(theme => ({
  container: {
    height: ['100vh', '100svh'],
    background: `${theme.palette.common.white}`,
    position: 'fixed',
    zIndex: 2101,
    left: 0,
    top: 0,
    width: '100%',
  },
  backgroundImage: {
    position: 'absolute',
    inset: 0,
    background: `url(${e3dcLightBackground}) no-repeat center`,
    opacity: 0.2,
    zIndex: -1,
    backgroundSize: 'auto 135%',
    [theme.breakpoints.up('sm')]: {
      backgroundSize: 'auto 97%',
    },
    [theme.breakpoints.up('md')]: {
      backgroundSize: '90% auto',
    },
  },
  e3dcLogo: {
    position: 'fixed',
    width: 132,
    left: '60px',
    top: '60px',
  },
  flowLogo: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '100%',
    marginTop: '-15px',
    [theme.breakpoints.up('sm')]: {
      width: 'auto',
      marginTop: 0,
    },
  },
}))

interface Props {
  children: React.ReactNode
}

/**
 * Wrapper to show a maintenance page based on feature flags and user level
 */
const MaintenancePageWrapper: React.FC<Props> = ({ children }) => {
  const { classes } = useStyles()
  const {
    featureFlags: { isMaintenancePageEnabled },
    isFeatureFlagsLoading,
  } = useFeatureFlagsContext()
  const navigate = useNavigate()

  const reload = (): void => navigate(0)

  if (isFeatureFlagsLoading) return <SuspenseContentFallback />
  if (isMaintenancePageEnabled) {
    return (
      <Box className={classes.container}>
        {!isFlowTheme && <Box className={classes.backgroundImage} />}
        <Box component="img" src={MAINTENANCE_LOGO} alt="logo" className={isFlowTheme ? classes.flowLogo : classes.e3dcLogo} />
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          <B2Sunrise sx={{ color: 'primary.main', height: 70, width: 70 }} />
          <Box sx={{ px: '5%', textAlign: 'center' }}>
            <Typography sx={{ pb: 2 }} variant="h2">
              {TITLE_GERMAN}
            </Typography>
            <Typography variant="h4">{SUBTITLE_GERMAN_1}</Typography>
            <Typography variant="h4">{SUBTITLE_GERMAN_2}</Typography>
          </Box>
          <Button onClick={reload} type="button" variant="contained">
            {BUTTON_TEXT_GERMAN}
          </Button>
        </Box>
        <Box sx={{ position: 'fixed', bottom: 60, width: '100%' }}>
          <Typography sx={{ textAlign: 'center', px: '5%' }} variant="h5">
            {FOOTER_ENGLISH_1}
            <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }} />
            {FOOTER_ENGLISH_2}
          </Typography>
        </Box>
      </Box>
    )
  }

  return <>{children}</>
}

export default MaintenancePageWrapper
